import { useEffect, useState } from 'react';
import { Gift, RefreshCw } from 'lucide-react';
import { api } from '../api.js';
import {
  Button, ExportButton, Field, FilterBar, Header, Modal, Pagination, Select, Status, Table,
  can, dateTime, number, op, queryString, useAsk, useOpsContext, useQuery,
} from './ui.jsx';

export function RedemptionsPage() {
  const { auth, stores, fail, success } = useOpsContext();
  const [filters, setFilters] = useState({ store_id: '', search: '', status: '', from: '', to: '' });
  const [page, setPage] = useState(1);
  const [form, setForm] = useState(null);
  const [ask, dialog] = useAsk();
  const list = useQuery(`${op}/redemptions${queryString({ ...filters, page })}`);
  const setFilter = (key, value) => { setFilters((current) => ({ ...current, [key]: value })); setPage(1); };

  const cancel = async (row) => {
    const values = await ask({
      title: 'Hủy giao dịch đổi quà?', danger: true, confirmLabel: 'Hủy và hoàn điểm',
      message: `${row.customer_name} · ${row.reward_name}. Hệ thống hoàn ${number.format(row.points_used)} điểm và ${row.quantity} quà về kho bằng giao dịch đảo; lịch sử cũ được giữ nguyên.`,
      fields: [{ key: 'reason', label: 'Lý do hủy', required: true, minLength: 3 }],
    });
    if (!values) return;
    try { await api.post(`${op}/redemptions/${row.id}/cancel`, { reason: values.reason }); success('Đã hoàn điểm và hoàn tồn kho'); list.reload(); } catch (error) { fail(error); }
  };

  return <main className="content-page">
    <Header eyebrow="LỊCH SỬ ĐỔI QUÀ" title="Đổi quà" description="Trừ điểm và trừ kho trong cùng một giao dịch; hủy bằng giao dịch đảo, không xóa lịch sử">
      {can(auth, 'redemptions.create') && <Button tone="primary" onClick={() => setForm({ store_id: filters.store_id || stores[0]?.id || '' })}><Gift size={16} /> Đổi quà</Button>}
    </Header>
    <FilterBar>
      <Select label="Cơ sở" value={filters.store_id} onChange={(value) => setFilter('store_id', value)} options={stores} placeholder="Tất cả cơ sở" />
      <Field label="Khách hàng" value={filters.search} onChange={(value) => setFilter('search', value)} placeholder="Tên hoặc số điện thoại" />
      <Select label="Trạng thái" value={filters.status} onChange={(value) => setFilter('status', value)} options={[{ id: 'completed', name: 'Hoàn tất' }, { id: 'cancelled', name: 'Đã hủy' }]} placeholder="Tất cả" />
      <Field label="Từ ngày" type="date" value={filters.from} onChange={(value) => setFilter('from', value)} />
      <Field label="Đến ngày" type="date" value={filters.to} onChange={(value) => setFilter('to', value)} />
      <ExportButton path={`${op}/redemptions${queryString({ ...filters, format: 'csv' })}`} filename="lich-su-doi-qua.csv" onError={fail} />
    </FilterBar>
    <section className="card table-card">
      <Table rows={list.rows} loading={list.loading} error={list.error} onRetry={list.reload} empty="Chưa có giao dịch đổi quà khớp bộ lọc"
        rowClass={(row) => (row.status === 'cancelled' ? 'row-void' : '')} columns={[
          { key: 'created_at', label: 'Thời gian', render: dateTime }, { key: 'phone_normalized', label: 'SĐT khách' }, { key: 'customer_name', label: 'Tên khách' },
          { key: 'store_name', label: 'Cơ sở' }, { key: 'reward_name', label: 'Món đổi', render: (v, row) => (Number(row.quantity) > 1 ? `${v} × ${row.quantity}` : v) },
          { key: 'points_before', label: 'Điểm trước', align: 'right', render: (v) => number.format(v) },
          { key: 'points_used', label: 'Điểm đổi', align: 'right', render: (v) => <span className="neg">{number.format(v)}</span> },
          { key: 'points_after', label: 'Còn lại', align: 'right', render: (v) => <span className="pos">{number.format(v)}</span> },
          { key: 'performed_by_name', label: 'Nhân viên' }, { key: 'notes', label: 'Ghi chú', render: (v, row) => (row.status === 'cancelled' ? `Hủy: ${row.cancel_reason}` : (v || '—')) },
          { key: 'status', label: 'Trạng thái', render: (v, row) => <>
            <Status value={v} />
            {row.review_flag === 'suspected_duplicate' && <> <Status value="overdue" label="Nghi trùng · cần kiểm tra" /></>}
            {row.source === 'legacy_import' && <> <Status value="inactive" label="Từ bảng cũ" /></>}
          </> },
        ]} actions={can(auth, 'redemptions.cancel') ? (row) => row.status === 'completed' && row.source !== 'legacy_import' && <button className="table-action delete" onClick={() => cancel(row)}>Hủy</button> : null} />
      <Pagination pagination={list.pagination} onPage={setPage} />
    </section>
    {form && <RedeemForm initialStore={form.store_id} stores={stores} fail={fail} onClose={() => setForm(null)} onDone={() => { setForm(null); success('Đổi quà thành công: đã trừ điểm và trừ kho'); list.reload(); }} />}
    {dialog}
  </main>;
}

function RedeemForm({ initialStore, stores, fail, onClose, onDone }) {
  const [storeId, setStoreId] = useState(initialStore);
  const [phone, setPhone] = useState('');
  const [lookup, setLookup] = useState({ state: 'idle', customer: null });
  const [itemId, setItemId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const stock = useQuery(storeId ? `${op}/reward-inventory?store_id=${storeId}` : null);
  const digits = phone.replace(/\D/g, '');
  const validPhone = /^(0\d{9}|84\d{9}|\d{9})$/.test(digits);

  useEffect(() => {
    if (!validPhone) { setLookup({ state: 'idle', customer: null }); return undefined; }
    let active = true;
    setLookup({ state: 'loading', customer: null });
    api.get(`${op}/customers/lookup?phone=${digits}${storeId ? `&store_id=${storeId}` : ''}`)
      .then((result) => { if (active) setLookup({ state: 'done', customer: result.customer }); })
      .catch(() => { if (active) setLookup({ state: 'done', customer: null }); });
    return () => { active = false; };
  }, [digits, validPhone, storeId]);

  const customer = lookup.customer;
  const item = (stock.rows || []).find((row) => Number(row.reward_item_id) === Number(itemId));
  const pointsNeeded = item ? Number(item.points_required) * Number(quantity || 0) : 0;
  const problems = [];
  if (lookup.state === 'done' && !customer) problems.push('Không tìm thấy khách với số điện thoại này. Hãy tạo hồ sơ ở trang Khách hàng trước.');
  if (customer && customer.status !== 'active') problems.push('Hồ sơ khách đang ở trạng thái Ngừng.');
  if (customer && item && Number(customer.points_balance) < pointsNeeded) problems.push(`Khách không đủ điểm: cần ${number.format(pointsNeeded)}, hiện có ${number.format(customer.points_balance)}.`);
  if (item && Number(item.quantity) < Number(quantity)) problems.push(`Cơ sở chỉ còn ${item.quantity} món này.`);
  const ready = customer && item && Number(quantity) >= 1 && !problems.length;

  const submit = async (event) => {
    event.preventDefault();
    if (!ready) return;
    setSaving(true);
    try {
      await api.post(`${op}/redemptions`, { store_id: Number(storeId), customer_id: Number(customer.id), reward_item_id: Number(itemId), quantity: Number(quantity), notes: notes.trim() || null });
      onDone();
    } catch (error) { fail(error); stock.reload(); } finally { setSaving(false); }
  };

  return <Modal title="Thực hiện đổi quà" onClose={onClose}>
    <form className="manager-form" onSubmit={submit}>
      <Select label="Cơ sở đổi" value={storeId} onChange={(value) => { setStoreId(value); setItemId(''); }} options={stores} required />
      <Field label="SĐT khách" value={phone} onChange={setPhone} required inputMode="numeric" autoFocus placeholder="Nhập số điện thoại để tra cứu"
        hint={lookup.state === 'loading' ? 'Đang tra cứu...' : customer ? `${customer.name} · ${number.format(customer.points_balance)} điểm` : null} />
      <Select label="Món đổi" value={itemId} onChange={setItemId} required
        options={(stock.rows || []).map((row) => ({ id: row.reward_item_id, name: `${row.name} · ${number.format(row.points_required)} điểm · còn ${row.quantity}` }))} />
      <Field label="Số lượng" type="number" min="1" max="100" value={quantity} onChange={setQuantity} required />
      <Field label="Ghi chú" value={notes} onChange={setNotes} wide />
      {customer && item && !problems.length && <div className="redeem-preview wide">
        <div><span>Điểm trước</span><strong>{number.format(customer.points_balance)}</strong></div>
        <div><span>Điểm đổi</span><strong className="neg">−{number.format(pointsNeeded)}</strong></div>
        <div><span>Còn lại</span><strong className="pos">{number.format(Number(customer.points_balance) - pointsNeeded)}</strong></div>
        <div><span>Tồn sau khi đổi</span><strong>{Number(item.quantity) - Number(quantity)}</strong></div>
      </div>}
      {problems.map((problem) => <p className="field-error wide" key={problem}>{problem}</p>)}
      <div className="form-actions wide"><Button type="button" onClick={onClose}>Đóng</Button><Button tone="primary" disabled={!ready || saving}>{saving && <RefreshCw className="spin" size={16} />} Xác nhận đổi</Button></div>
    </form>
  </Modal>;
}
